import { useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { NotificationService } from '../services/notification/notification.service';
import type {
  INotificationService,
  NotificationListParams,
} from '../services/notification/notification.service.interface';

const BELL_PAGE_SIZE = 10;

export const notificationQueryKeys = {
  all: ['notifications'] as const,
  bell: () => ['notifications', 'bell'] as const,
  list: (params: NotificationListParams) => ['notifications', 'list', params] as const,
};

/** Paginated + filtered list for the notifications page. */
export function useNotificationsList(params: NotificationListParams) {
  const service = useInjection<INotificationService>(NotificationService);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: notificationQueryKeys.list(params),
    queryFn: () => service.getNotifications(params),
    refetchOnWindowFocus: true,
  });

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    unreadCount: data?.unreadCount ?? 0,
    page: data?.page ?? params.page ?? 1,
    pageSize: data?.pageSize ?? params.pageSize ?? BELL_PAGE_SIZE,
    isLoading,
    isError,
    refetch,
  };
}

/** Recent slice + unread badge for the nav bell. Kept as its own cache key so it
 * polls independently of whatever filters the page has active. */
export function useNotificationBell() {
  const service = useInjection<INotificationService>(NotificationService);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: notificationQueryKeys.bell(),
    queryFn: () => service.getNotifications({ page: 1, pageSize: BELL_PAGE_SIZE }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  return {
    items: data?.items ?? [],
    unreadCount: data?.unreadCount ?? 0,
    isLoading,
    isError,
    refetch,
  };
}

export function useUnreadCount() {
  return useNotificationBell().unreadCount;
}
